<template>
  <div>
    <WarehousePurchaseStatusDialog
      v-model="dialogStatus"
      :editedItem="editedItem"
      @success="onSuccess"
    ></WarehousePurchaseStatusDialog>
    <warehouse-purchase-order-dialog
      v-model="purchaseOrderDialog"
      :editedItem="editedItem"
      :is-purchase-order="purchaseOrder"
      @success="onSuccess"
    >
    </warehouse-purchase-order-dialog>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="group.warehouseName"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['supplier']"
        >
          <template v-slot:item.document_type="{ item }">
            <span>{{ documentTypes[item.document_type] }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="getStatusColor(item.status)">
              {{ $t(statusKeyMap[item.status]) }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                class="mr-2"
                icon="mdi-file-document-arrow-right-outline"
                :tooltip="$t('warehouse.supplier_delivered')"
                @click="
                  editedItem = item;
                  dialogStatus = true;
                "
              />
              <circle-button
                icon="mdi-eye"
                tooltip="Pregled"
                class="mr-2"
                @click="editItem(item)"
              ></circle-button>
              <remove-button @click="deleteItem(item)"></remove-button>
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-cart-arrow-down"
        :title="$t('no_purchases')"
        :subtitle="$t('no_purchases_yet')"
      >
        <warehouse-button-order @order="orderGroceries($event)"></warehouse-button-order>
      </empty-state>

      <div class="fab-container">
        <v-btn color="primary" class="mb-2" block @click="orderGroceries(true)">
          <v-icon left>mdi-cart-arrow-down</v-icon>
          {{ $t("warehouse.purchase_order") }}
        </v-btn>
        <v-btn color="info" block @click="orderGroceries(false)">
          <v-icon left>mdi-file-document-outline</v-icon>
          {{ $t("warehouse.request_quote") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <v-data-table
        v-if="filteredDocuments.length > 0 || loading"
        :headers="headers"
        :items="filteredDocuments"
        class="striped-table ordera-box-shadow"
        :loading="loading"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer></v-spacer>
            <warehouse-button-order
              @order="orderGroceries($event)"
            ></warehouse-button-order>
          </v-toolbar>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              class="mr-2"
              icon="mdi-file-document-arrow-right-outline"
              :tooltip="$t('warehouse.supplier_delivered')"
              @click="
                editedItem = item;
                dialogStatus = true;
              "
            />
            <circle-button
              icon="mdi-eye"
              :tooltip="$t('overview')"
              class="mr-2"
              color="primary"
              @click="editItem(item)"
            ></circle-button>
            <remove-button @click="deleteItem(item)"></remove-button>
          </v-row>
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.status="{ item }">
          <autocomplete
            hide-details
            color="white"
            v-model="item.status"
            :items="statuses"
            item-text="name"
            item-value="value"
            @change="updateStatus(item)"
          ></autocomplete>
        </template>
        <template v-slot:item.document_type="{ item }">
          <span>{{ documentTypes[item.document_type] }}</span>
        </template>
      </v-data-table>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-cart-arrow-down"
        :title="$t('no_purchases')"
        :subtitle="$t('no_purchases_yet')"
      >
        <warehouse-button-order @order="orderGroceries($event)"></warehouse-button-order>
      </empty-state>
    </template>

    <!-- Removed local v-snackbar -->
    <global-confirm-box ref="confirmBox"></global-confirm-box>
  </div>
</template>

<script>
import WarehousePurchaseStatusDialog from "@/components/warehouse/purchase/dialogs/WarehousePurchaseStatusDialog.vue";
import { WarehousePurchaseOrderDialog } from "@/components/warehouse/requirement";
import { WarehouseButtonOrder } from "@/components/warehouse/buttons";
import { orderDocumentGrocery, requestOrderDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import warehouseService from "@/services/warehouse";

export default {
  components: {
    WarehousePurchaseStatusDialog,
    WarehousePurchaseOrderDialog,
    WarehouseButtonOrder,
    EmptyState,
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasWarehousePrinting, hasExcel],
  data() {
    return {
      documents: [],
      dialogStatus: false,
      purchaseOrder: false,
      purchaseOrderDialog: false,
      editedItem: JSON.parse(JSON.stringify(orderDocumentGrocery)), // Deep copy for default
      loading: false,
    };
  },

  created() {
    this.load();
    this.$store.dispatch("warehouse/fetchSuppliers").catch(() => {
      this.showErrorSnackbar(this.$t("error_loading_suppliers"));
    });
  },

  methods: {
    getStatusColor(status) {
      const colors = {
        created: "grey",
        sent: "blue",
        confirm: "success",
        "confirm-half": "warning",
        "reject-supplier": "error",
        "changed-in-progress": "orange",
        "waiting-to-other-supplier": "purple",
        reject: "error",
        delivered: "success",
        "delivered-half": "warning",
        "on-checking": "info",
        "reject-on-arrived": "error",
        accept: "success",
        closed: "grey darken-2",
      };
      return colors[status] || "grey";
    },

    async updateStatus(item) {
      const request = warehouseService.updateWarehouse(item.id, {
        status: item.status,
      });
      try {
        await this.handleRequest(request, {
          successMessage: this.$t("purchase_status_updated"),
          errorMessagePrefix: this.$t("error_updating_purchase_status") + ": ",
        });
        // Optionally, reload or update local data if needed, though status is bound
      } catch (error) {
        this.load();
        console.error("Failed to update status", error);
      }
    },

    editItem(item) {
      this.editedItem = JSON.parse(JSON.stringify(item)); // Deep copy
      this.purchaseOrderDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_document"),
          this.$t("warehouse.confirm_delete_document"),
          {
            color: "error",
          }
        )
        .catch(() => false);

      if (confirmed) {
        const request = warehouseService.removeWarehouse(item.id);
        try {
          await this.handleRequest(request, {
            successMessage: this.$t("purchase_document_deleted"),
            errorMessagePrefix: this.$t("error_deleting_purchase_document") + ": ",
          });
          const index = this.documents.findIndex((document) => document.id === item.id);
          if (index !== -1) {
            this.documents.splice(index, 1);
          }
        } catch (error) {
          console.error("Failed to delete item", error);
        }
      }
    },

    orderGroceries(isPurchaseOrder) {
      this.purchaseOrder = isPurchaseOrder;
      this.editedItem = isPurchaseOrder
        ? JSON.parse(JSON.stringify(orderDocumentGrocery)) // Deep copy
        : JSON.parse(JSON.stringify(requestOrderDocumentGrocery)); // Deep copy
      this.purchaseOrderDialog = true;
    },

    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    async load() {
      this.loading = true;
      try {
        const [orderResponse, requestResponse] = await Promise.all([
          warehouseService.getWarehouseDocuments({ document_type: "order" }),
          warehouseService.getWarehouseDocuments({ document_type: "request" }),
        ]);
        this.documents = orderResponse.data
          .concat(requestResponse.data)
          .map((item) => ({
            ...item,
            groceries: item.groceries.map((grocery) => ({
              ...grocery,
              order: grocery.quantity,
            })),
          }))
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } catch (error) {
        console.error("Error loading documents:", error);
        this.showErrorSnackbar(this.$t("error_loading_purchase_documents"));
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    filteredDocuments() {
      const documents = this.documents.map((document) => {
        return {
          ...document,
          statusFilter: this.translate(
            this.statuses.find((status) => status.value === document.status)?.name
          ),
          typeFilter: this.translate(this.documentTypes[document.document_type]),
          supplier:
            this.$store.state.warehouse.suppliers.find(
              (supplier) => supplier.id === document.supplier_id
            )?.name || this.$t("warehouse.unknown_supplier"),
        };
      });
      return this.customSearch(
        documents,
        [
          ...this.headers,
          {
            text: this.$t("warehouse.document_type"),
            value: "typeFilter",
          },
          {
            text: this.$t("status"),
            value: "statusFilter",
          },
        ],
        this.filters.search
      ).filter((item) => {
        const documentDate = new Date(item.document_date + " 12:00:00");
        const { from, to } = this.filters.dates;
        const dateStart = new Date(from + " 00:00:00");
        const dateEnd = new Date(to + " 23:59:59");
        const statusFilter = !this.filters.status || item.status === this.filters.status;
        const dateFilter =
          !from || !to || (documentDate >= dateStart && documentDate <= dateEnd);
        const typeFilter =
          !this.filters.documentType || item.document_type === this.filters.documentType;
        const warehouseFilter = this.filters.warehouse.some(
          (warehouse) => warehouse.id === item.warehouse_id
        );
        const supplierFilter = this.filters.suppliers.some(
          (supplier) => supplier.id === item.supplier_id
        );
        return (
          dateFilter && typeFilter && statusFilter && warehouseFilter && supplierFilter
        );
      });
    },
    headers() {
      return [
        { text: "ID", align: "start", value: "id", mobile: false, width: "70px" },
        { text: this.$t("stock"), value: "warehouse.name", mobile: false },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 4 },
        { text: this.$t("warehouse.document_type"), value: "document_type", cols: 4 },
        { text: this.$t("status"), value: "status", cols: 4 },
        { text: this.$t("date"), value: "document_date", cols: 4 },
        { text: this.$t("supplier"), value: "supplier", cols: 4 },
        {
          text: this.$t("warehouse.offers_deadline"),
          value: "offer_deadline",
          mobile: false,
        },
        {
          text: this.$t("warehouse.delivery_deadline"),
          value: "delivery_deadline",
          mobile: false,
        },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 4,
          width: "150px",
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    documentsByWarehouse() {
      const groups = {};
      this.filteredDocuments.forEach((item) => {
        const warehouseId = item.warehouse_id;
        if (!groups[warehouseId]) {
          groups[warehouseId] = {
            warehouseName: item.warehouse?.name || this.$t("warehouse.unknown"),
            items: [],
          };
        }
        groups[warehouseId].items.push(item);
      });
      return groups;
    },
    documentTypes() {
      return {
        order: this.$t("warehouse.purchase_order"),
        request: this.$t("warehouse.request_quote"),
        entry: this.$t("warehouse.entry"),
      };
    },
    statusKeyMap() {
      return {
        created: "status_created",
        sent: "status_sent",
        confirm: "status_confirmed_by_supplier",
        "confirm-half": "status_partially_confirmed_by_supplier",
        "reject-supplier": "status_rejected_by_supplier",
        "changed-in-progress": "status_change_in_progress",
        "waiting-to-other-supplier": "status_waiting_for_another_supplier",
        reject: "status_rejected_by_us",
        delivered: "status_delivered",
        "delivered-half": "status_partially_delivered",
        "on-checking": "status_on_checking",
        "reject-on-arrived": "status_rejected_on_arrival",
        accept: "status_accepted",
        closed: "status_closed",
        undefined: "status_undefined",
      };
    },
    statuses() {
      return Object.entries(this.statusKeyMap).map(([statusValue, translationKey]) => ({
        name: this.$t(translationKey), // Use the translation key
        value: statusValue,
      }));
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    exportItems() {
      return this.filteredDocuments.map((item) => ({
        ...item,
        status: this.translate(
          this.statuses.find((status) => status.value === item.status)?.name
        ),
        document_type: this.translate(this.documentTypes[item.document_type]),
      }));
    },
    exportTitle() {
      return [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.ordered").toLowerCase(),
      ].join(" ");
    },
  },

  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    purchaseOrderDialog(val) {
      if (!val) {
        this.editedItem = JSON.parse(JSON.stringify(orderDocumentGrocery));
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [
            {
              name: this.exportTitle,
              headers: this.headers,
              items: this.exportItems,
              title: this.exportTitle,
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
    isPrinting(isPrinting) {
      if (isPrinting) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [
            {
              headers: this.headers,
              items: this.exportItems,
            },
          ],
        });
      }
    },
  },
};
</script>

<style scoped>
.fab-container {
  position: fixed;
  bottom: 16px;
  left: 16px;
  right: 16px;
  z-index: 5;
}
</style>
